import { Alert, Box, Button, Stack, useTheme } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { CopyToClipboardButton, DynamicIcon, SidePanelCard } from 'src/components';
import { getEntityCardIconSx, getSidePanelCardsSx } from 'src/components/EntitySidePanel';
import { BlockContainer } from 'src/components/Layout/named-layout-block.view';
import { PersonalizedSections } from 'src/components/SidePanelPersonalization';
import { PersonalizeSidePanelButton } from 'src/components/SidePanelPersonalization/personalize-side-panel-button';
import { resolvePartnerTabContactInfo } from 'src/components/SidePanelTabs/PartnerTab/partner-contact-info';
import { PartnerInfo } from 'src/components/SidePanelTabs/PartnerTab/partner-info';
import { PartnerInfoTabs } from 'src/components/SidePanelTabs/PartnerTab/partner-info-tabs';
import { SiteRelatedTabs } from 'src/components/SidePanelTabs/SiteTab/site-related-tabs';
import { SiteTab as SiteTabComponent } from 'src/components/SidePanelTabs/SiteTab/site-tab';
import { SiteMenu } from 'src/components/SiteComponents/site-menu';
import { SiteShareButton } from 'src/components/SiteComponents/site-share-button';
import { Address, useGroupRules, useRestoreAddress, UserGroupRules } from 'src/graphql';
import { CustomAttributesSidePanelProvider } from 'src/graphql/entities/custom-attributes/side-panel-provider';
import { SidePanelPersonalizationProvider } from 'src/graphql/entities/side-panel-personalization';
import { CrossPartnerUserSettingsProvider } from 'src/graphql/entities/side-panel-personalization/cross-partner-user-settings-provider';
import { PartnerCard } from 'src/pages/Conversations/SidePanel/partner-card';
import { SidePanelToolsSlotProvider } from 'src/pages/Conversations/SidePanel/side-panel-tools-slot';
import { SiteCardFormProvider } from 'src/pages/Sites/SidePanel/site-card-form-provider';
import { siteSidePanelSurface } from 'src/pages/Sites/SidePanel/site-surface';
import { useRegisterConversationModalDefaults } from 'src/pages/Sites/use-register-conversation-modal-defaults';
import { useSiteQuickActions } from 'src/pages/Sites/use-site-quick-actions';
import { useGlobalStore } from 'src/store';
import { useScreenDimensions } from 'src/utils/system';

interface SiteSidePanelProps {
  site: Address;
  // hosts now own the panel chrome (and its close control) directly; kept for prop-contract compatibility
  // eslint-disable-next-line react/no-unused-prop-types
  onClose?: () => void;
  extendedView?: boolean;
  /** Sticky node in the scrolling column that hosts the floating panel tools. */
  toolsSlot?: HTMLElement | null;
}

const SiteCardBody = ({
  address,
  extendedView,
}: {
  address: Address;
  extendedView?: boolean;
}) => {
  const { isMobile } = useScreenDimensions();
  const isDeleted = !!address?.isDeleted;

  const { checkValidity } = useGroupRules();
  const { restoreAddress } = useRestoreAddress();

  const onRestoreSiteClick = useCallback(async () => {
    await restoreAddress(address.addressId);
  }, [restoreAddress, address.addressId]);

  return (
    <SiteTabComponent address={address} disabled={isDeleted} propertiesAsRows hideAddressRow>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
        <CopyToClipboardButton content={address.fullAddress!} />
        <PersonalizeSidePanelButton />
        <SiteShareButton
          address={address}
          partnerId={address?.partner?.partnerId ?? address?.partnerId}
        />
        {isMobile ? <SiteMenu address={address} /> : null}
      </Stack>

      {isDeleted ? (
        <Alert
          severity="info"
          action={
            checkValidity([UserGroupRules.AccessToRestoreDeletedAddresses]) ? (
              <Button color="inherit" size="small" onClick={onRestoreSiteClick}>
                RESTORE
              </Button>
            ) : null
          }
        >
          This site was deleted
        </Alert>
      ) : null}

      {/* attribute rows continue the card's property rhythm — cancel the wrapper
          Stack's spacing step (same treatment as the labels Box in SiteTab) */}
      <Box sx={{ marginTop: (theme) => theme.utils.important(0) }}>
        <PersonalizedSections />
      </Box>

      {!extendedView && <SiteRelatedTabs address={address} variant="scrollable" />}
    </SiteTabComponent>
  );
};

export const SiteSidePanel = ({ extendedView, site, toolsSlot }: SiteSidePanelProps) => {
  const theme = useTheme();
  const { isMobile } = useScreenDimensions();
  const isMultiTenancyMode = useGlobalStore.use.isMultiTenancyMode();
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { groupRules, checkValidity } = useGroupRules();
  // without a slot the personalize button falls back to rendering inline in the card,
  // so the clearance above the cards would reserve room for nothing
  const hasFloatingTools =
    !!toolsSlot && checkValidity([UserGroupRules.AccessToPersonalizeSidepanel]);
  const canAccessToPartnerTab = !!groupRules[UserGroupRules.AccessToPartnerTab];
  const canSeePartnersTab =
    canAccessToPartnerTab || (isMultiTenancyMode && selectedPartnerId === null);

  const partnerWithBrandGroupContactInfo = useMemo(
    () => resolvePartnerTabContactInfo(site.partner, site.brand?.brandGroupId),
    [site.brand?.brandGroupId, site.partner],
  );

  useSiteQuickActions(site);

  useRegisterConversationModalDefaults(site);

  return (
    <CrossPartnerUserSettingsProvider>
      <CustomAttributesSidePanelProvider
        entity="Address"
        entityId={site.addressId}
        partnerId={site.partner?.partnerId ?? site.partnerId}
        canEdit={!site.isDeleted}
      >
        <SiteCardFormProvider address={site} disabled={!!site.isDeleted}>
          <SidePanelPersonalizationProvider surface={siteSidePanelSurface}>
            <BlockContainer
              // the cards inside carry the only frame, so the column itself stays bare
              border="none !important"
              sx={{
                height: 'auto',
                // the collapsing wrapper sets nowrap for its width animation; undo it here or the content outgrows the column
                whiteSpace: 'normal',
              }}
            >
              <SidePanelToolsSlotProvider value={toolsSlot ?? null}>
                <Stack
                  data-testid="site-side-panel-cards"
                  gap={1}
                  sx={getSidePanelCardsSx({ hasFloatingTools, extendedView, isMobile })}
                >
                  {canSeePartnersTab && site.partner ? (
                    <PartnerCard partner={site.partner} data-testid="site-partner-card">
                      <Stack spacing={2}>
                        <PartnerInfo partner={partnerWithBrandGroupContactInfo} />
                        {partnerWithBrandGroupContactInfo ? (
                          <PartnerInfoTabs
                            partner={partnerWithBrandGroupContactInfo}
                            address={site}
                          />
                        ) : null}
                      </Stack>
                    </PartnerCard>
                  ) : null}
                  <SidePanelCard
                    data-testid="site-details-card"
                    defaultOpen
                    leading={
                      <DynamicIcon
                        name="home"
                        library="lucide"
                        sx={getEntityCardIconSx(theme.palette.custom.OV.entity.site)}
                      />
                    }
                    title={site.fullAddress}
                    titleTooltip={site.fullAddress ?? undefined}
                  >
                    <SiteCardBody address={site} extendedView={extendedView} />
                  </SidePanelCard>
                </Stack>
              </SidePanelToolsSlotProvider>
            </BlockContainer>
          </SidePanelPersonalizationProvider>
        </SiteCardFormProvider>
      </CustomAttributesSidePanelProvider>
    </CrossPartnerUserSettingsProvider>
  );
};
